/**
 * The field-type vocabulary — the closed set of prop shapes a block may declare.
 *
 * **This file is the spine of the product.** A registry entry declares its props from THIS
 * vocabulary, and that one declaration drives five things without restating anything:
 *
 *   1. validation                (`validate.ts` walks the fields)
 *   2. the editor form           (`@ksa/ui`'s field renderers — ONE control per field kind, never a
 *                                 form per block)
 *   3. the renderer's contract   (`@ksa/render`'s block components)
 *   4. the AI tool schema        (`ai-schema.ts` compiles a BlockDef to JSON Schema)
 *   5. the seed/default builder  (`defaultProps`)
 *
 * The payoff is that a new block ships with **zero** editor code and **zero** new form. Add the
 * `list` field kind once, and every list — a footer's branches, a team grid, a gallery — gets
 * add/remove/reorder for free.
 *
 * The rule that keeps it true: **a block may not invent a field kind.** If a block needs something
 * this file does not offer, the answer is to add it here, to all five consumers, once.
 */

import type { Text } from './text.ts'

/**
 * The site theme's colour roles.
 *
 * Role PAIRS, not `primary`/`secondary`: `on-brand` is the text colour that sits on `brand`, so
 * contrast is *structural* rather than something a customer has to check. `@ksa/render`'s templates
 * map these names to actual colours; the model only ever knows the names.
 */
export const SITE_ROLES = [
  'brand',
  'on-brand',
  'surface',
  'on-surface',
  'muted',
  'accent',
  'on-accent',
  'border',
] as const
export type SiteRole = (typeof SITE_ROLES)[number]

/**
 * The roles a block may use as a BACKGROUND.
 *
 * ⚠️ THIS NARROW LIST IS A BUG FIX INHERITED FROM THE PREDECESSOR, AND IT IS WORTH THE PARAGRAPH.
 * There, the `background` field offered every member of `SITE_ROLES`, while the two blocks that read
 * it resolved their text as `background === 'brand' ? on-brand : on-surface`. So every ground except
 * `brand` silently got `on-surface`, whatever it actually was. Measured on the shipped palettes:
 *
 *     on-surface   1.00:1   — the SAME colour as the text. Invisible.
 *     muted        2.68:1
 *     accent       3.26:1
 *
 * All three were one dropdown click away, on the hero, the first block of the first page of every
 * site. "Contrast is structural" was a claim about the token *names*; nothing enforced it at the
 * point a ground was chosen.
 *
 * **So a ground is only a role that HAS an `on-` partner, and `onRole` is the one place that maps
 * one to the other.** A role with no partner is not a background, and offering it was the bug.
 *
 * ⚠️ `accent` IS A BUTTON GROUND AND NOT A SECTION GROUND — measured, not stylistic. `hero` and `cta`
 * soften their supporting line with `opacity: 0.85`, which composites the text toward its ground; on
 * `accent`, even pure white lands at 3.98:1. No `on-accent` fixes that, because the ceiling is the
 * ground's own lightness. A button has no softened text, so the same pair is fine there at 4.67:1.
 * `check:contrast` tests the composited pairs precisely because a table of hexes cannot see opacity.
 */
export const GROUND_ROLES = ['surface', 'brand'] as const
export type GroundRole = (typeof GROUND_ROLES)[number]

const ON_ROLE: Record<string, SiteRole> = {
  surface: 'on-surface',
  brand: 'on-brand',
  accent: 'on-accent',
}

/**
 * The text colour for a ground.
 *
 * Falls back to `on-surface` for anything unrecognised — which is what a document authored before
 * the grounds were narrowed carries. The fallback is the safe direction (`surface` is the page's own
 * background) and it is deliberately **not silent**: `validateSite` reports the stale value, so the
 * document is told rather than quietly rendered in a colour nobody chose.
 */
export function onRole(ground: string): SiteRole {
  return ON_ROLE[ground] ?? 'on-surface'
}

/**
 * Whether a prop survives a template switch.
 *
 * `core` props are the portable contract — the headline, the photograph, the button's destination.
 * `tmpl` props are the template's own — a background choice, a density — and drop to the new
 * template's defaults when the template changes. That is what makes "try another look" a
 * non-destructive click rather than a decision a customer has to be warned about.
 */
export type Scope = 'core' | 'tmpl'

export type EnumOption = { value: string; label: Text }

export type FieldType =
  | { kind: 'text' }
  /** Multi-paragraph prose. Same `Text` leaf; a different control and a different length budget. */
  | { kind: 'longText' }
  | { kind: 'image' }
  | { kind: 'link' }
  /** A background role. Constrained to `GROUND_ROLES` — see above. */
  | { kind: 'colorToken' }
  | { kind: 'enum'; options: EnumOption[] }
  | { kind: 'number'; min?: number; max?: number }
  | { kind: 'boolean' }
  /** A repeatable row of sub-fields. Bounded, because an AI asked for "a few features" will happily
   *  emit forty and a customer cannot delete them faster than the model writes them. */
  | { kind: 'list'; item: FieldSpec[]; min?: number; max?: number }

export type FieldSpec = {
  key: string
  label: Text
  type: FieldType
  /** Defaults to `'core'`. */
  scope?: Scope
  default: unknown
  hint?: Text
  /** Whether publish should complain when this is empty. Editing never blocks on it — a half-built
   *  site is the normal state of a site being built. */
  requiredForPublish?: boolean
  /**
   * Another field on this block that must be shown WITH this one when the drawer opens on this one
   * alone.
   *
   * ⚠️ IT EXISTS BECAUSE A BUTTON IS ONE THING TO A CUSTOMER AND TWO FIELDS TO US. Tapping a button
   * in the preview matches its words, so the drawer opened on `ctaLabel` — and its destination, the
   * other half of the same object, was not on screen at all and had no route to it except backing
   * out to the whole section. "I cannot change where it goes" was the report.
   *
   * ⚠️ AND IT IS DECLARED HERE RATHER THAN INFERRED FROM THE NAMES. `ctaLabel`/`ctaLink` is a
   * convention, and a convention read by a regular expression in the editor is a rule nobody can see
   * from the block that depends on it — the next `Label`/`Link` pair that does not want the pairing,
   * or that spells it differently, breaks silently. One declaration, one consumer that reads it
   * (CLAUDE.md §0.5).
   */
  alongside?: string
}

export type Variant = { value: string; label: Text }

/**
 * A block's registry metadata — **pure data, no React**, so the API, the Worker and the AI router
 * can all import it. The render component that implements this contract lives in `@ksa/render` and
 * is keyed separately by `type`; keeping the two maps apart is what keeps this file DOM-free.
 */
export type BlockDef = {
  type: string
  /** Schema version these fields describe. Bumped when props change shape; the renderer keeps old
   *  versions alive because published sites cannot be migrated synchronously. */
  v: number
  label: Text
  /** `site` blocks are singletons on the site document (navbar, footer); `page` blocks live in a
   *  page's flat array. */
  scope: 'site' | 'page'
  /** `fullOnly` blocks always span the page and offer no width control — a full-bleed hero, a CTA
   *  band. Declared on the block rather than hard-coded in the editor. */
  fullOnly?: boolean
  /**
   * ⚠️ THESE THREE ARE THE AI CONTRACT, and `whenNotToUse` is the one that earns its keep. Without
   * it a model reaches for `hero` for every section, because "prominent opening section" reads as
   * "important section" to something that has not seen the page. They are carried on the BlockDef —
   * not in a separate prompt file — so a new block cannot ship without them.
   */
  description: string
  whenToUse: string
  whenNotToUse: string
  variants: Variant[]
  fields: FieldSpec[]
}

/** Build a block's default prop bag from its field defaults — used when a block is added. */
export function defaultProps(def: BlockDef): Record<string, unknown> {
  const props: Record<string, unknown> = {}
  for (const f of def.fields) props[f.key] = clone(f.default)
  return props
}

/**
 * Build one blank ROW of a `list` field from its sub-field defaults.
 *
 * ⚠️ THE SAME FUNCTION THE EDITOR'S "ADD" USES AND THE ONE THE PREVIEW'S `+` USES. There are now two
 * places a customer can add a row — the list control in the drawer, and the button hanging off the
 * list in the rendered page — and a second implementation of "what a new row contains" is how one of
 * them starts producing rows that are missing a field the other has.
 */
export function defaultRow(fields: FieldSpec[]): Record<string, unknown> {
  const row: Record<string, unknown> = {}
  for (const f of fields) row[f.key] = clone(f.default ?? null)
  return row
}

/** The default variant is the first declared one; a block with no variants uses undefined. */
export function defaultVariant(def: BlockDef): string | undefined {
  return def.variants[0]?.value
}

/** A dependency-free deep clone for the plain JSON values defaults are made of. `structuredClone`
 *  exists everywhere this runs; the narrow helper documents that a default is always plain data —
 *  a default carrying a function or a Date would be a bug this makes loud. */
function clone<T>(value: T): T {
  return value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T)
}
